import { useQuery } from '@tanstack/react-query';
import type {
  CalendarEvent,
  CalendarEventInput,
  CalendarFreeSlotsInput,
  FreeSlotsResult,
  CalendarOccurrence,
  CalendarResponse,
  CalendarSeriesScope,
} from '@avslack/shared';
import { apiFetch } from '../lib/api';

/**
 * NAPTAR (versenyaudit H1) - a kliens oldal.
 *
 * A naptar egy idoszak ALKALMAIT kerdezi (az ismetlodo sorozatot a szerver
 * bontja ki). A valtozasrol a valos ideju reteg szol (`calendar.changed`), es
 * ilyenkor minden betoltott naptar ujratoltodik.
 */

export type CalendarSource =
  { kind: 'own' } | { kind: 'channel'; id: string } | { kind: 'group'; id: string };

export const calendarKey = ['calendar'] as const;

const post = <T>(path: string, body: unknown, method = 'POST') =>
  apiFetch<T>(path, { method, body: JSON.stringify(body) });

export const listCalendar = (from: Date, to: Date, source: CalendarSource) => {
  const params = new URLSearchParams({ from: from.toISOString(), to: to.toISOString() });
  if (source.kind === 'channel') params.set('channelId', source.id);
  if (source.kind === 'group') params.set('groupId', source.id);
  return apiFetch<{ occurrences: CalendarOccurrence[] }>(`/api/calendar?${params}`).then(
    (result) => result.occurrences,
  );
};

export const getCalendarEvent = (id: string) =>
  apiFetch<{ event: CalendarEvent }>(`/api/calendar/events/${id}`).then((r) => r.event);

export const createCalendarEvent = (input: CalendarEventInput) =>
  post<{ event: CalendarEvent }>('/api/calendar/events', input);

/**
 * Ismetlodo sorozatnal MIRE vonatkozik a modositas / torles - mint az
 * Outlookban (docs/23). Az alkalmat az EREDETI kezdete azonositja.
 */
export interface SeriesTarget {
  scope: CalendarSeriesScope;
  occurrence?: string;
}

const targetQuery = (target: SeriesTarget | undefined, extra: Record<string, string> = {}) => {
  const params = new URLSearchParams(extra);
  if (target) {
    params.set('scope', target.scope);
    if (target.occurrence && target.scope !== 'all') params.set('occurrence', target.occurrence);
  }
  const text = params.toString();
  return text ? `?${text}` : '';
};

export const updateCalendarEvent = (id: string, input: CalendarEventInput, target?: SeriesTarget) =>
  post<{ event: CalendarEvent }>(
    `/api/calendar/events/${id}${targetQuery(target)}`,
    input,
    'PATCH',
  );

/** Torles: `me` = csak a sajat naptarambol, `everyone` = lemondas (csak a szervezo). */
export const deleteCalendarEvent = (
  id: string,
  target: SeriesTarget & { for: 'me' | 'everyone' },
) =>
  apiFetch<{ ok: boolean }>(
    `/api/calendar/events/${id}${targetQuery(target, { for: target.for })}`,
    { method: 'DELETE' },
  );

/** Szabad idopont keresese egy csatorna / csoport tagjainak (docs/23). */
export const findFreeSlots = (input: CalendarFreeSlotsInput) =>
  post<FreeSlotsResult>('/api/calendar/free-slots', input);

export const respondCalendarEvent = (id: string, response: CalendarResponse) =>
  post<{ response: CalendarResponse }>(`/api/calendar/events/${id}/respond`, { response });

/** Egy bejegyzes a resztvevokkel es a sajat valasszal (a kartya es a reszletek ebbol rajzol). */
export function useCalendarEvent(id: string | null) {
  return useQuery({
    queryKey: [...calendarKey, 'event', id],
    queryFn: () => getCalendarEvent(id!),
    enabled: Boolean(id),
    staleTime: 30_000,
    retry: false,
  });
}

/** A megvalaszolatlan meghivok szama - a Naptar menupont jelzese. */
export function usePendingInvites() {
  return useQuery({
    queryKey: [...calendarKey, 'pending'],
    queryFn: () => apiFetch<{ count: number }>('/api/calendar/pending').then((r) => r.count),
    staleTime: 60_000,
  });
}

export const getCalendarFeed = () =>
  apiFetch<{ active: boolean; createdAt: string | null; lastUsedAt: string | null }>(
    '/api/calendar/feed',
  );

export const createCalendarFeed = () =>
  apiFetch<{ url: string; webcalUrl: string }>('/api/calendar/feed', { method: 'POST' });

export const revokeCalendarFeed = () =>
  apiFetch<{ ok: boolean }>('/api/calendar/feed', { method: 'DELETE' });

export function useCalendar(from: Date, to: Date, source: CalendarSource) {
  return useQuery({
    queryKey: [...calendarKey, source, from.toISOString(), to.toISOString()],
    queryFn: () => listCalendar(from, to, source),
    staleTime: 60_000,
  });
}

// ─── Kezdes elotti emlekezteto ────────────────────────────────────────────────

export interface CalendarReminder {
  eventId: string;
  title: string;
  startsAt: string;
  withCall: boolean;
  channelId: string | null;
  scope: 'channel' | 'group';
  groupId: string | null;
}

type Handler = (reminder: CalendarReminder) => void;
const handlers = new Set<Handler>();

export function onCalendarReminder(handler: Handler): () => void {
  handlers.add(handler);
  return () => handlers.delete(handler);
}

export function emitCalendarReminder(reminder: CalendarReminder): void {
  for (const handler of handlers) handler(reminder);
}
